import { useEffect, useContext, useState } from 'react'
import _ from 'lodash'
import { InitContext } from 'contexts/InitContext'
import {
  getPartnerId,
  getToken,
  setPartnerId,
  setToken,
  setUserId,
} from 'utils/authLocalStorage'
import { useLoginUrl } from 'hooks/basicHooks'
import { ResourceContext } from 'contexts/ResourceContext'
import {
  getWWUserId,
  getWWBmi,
  setWWAge,
  setWWBmi,
  setWWClientId,
  setWWClientSecret,
  setWWFeatures,
  setWWGender,
  setWWHeight,
  setWWTitlebar,
  setWWUserId,
  setWWWeight,
} from 'utils/wwLocalStorage'
import {
  amplitudeInit,
  logAmplitudeEvent,
  setAmplitudeProfile,
} from 'utils/amplitudeInit'
import { loginWithPartnerPromise, uploadProfilePromise } from 'apis/Auth'
import {
  addGoogleTagManager,
  getErrorPagePath,
  isMuscleDefense,
  PARTNER_ID,
} from 'utils/basic'
import { AuthContext } from 'contexts/AuthContext'

export const useAutoLogin = () => {
  const initContext = useContext(InitContext)
  const rContext = useContext(ResourceContext)
  const [autoLoginLoading, setAutoLoginLoading] = useState(true)
  const authContext = useContext(AuthContext)
  const {
    clientId,
    guid,
    clientSecret,
    titlebar,
    age,
    bmi,
    gender,
    weight,
    height,
    features,
    planId,
  } = useLoginUrl()

  if (clientId && guid && clientSecret) {
    setWWTitlebar(titlebar === '1' ? '1' : '0')
    setWWClientId(clientId)
    setWWClientSecret(clientSecret)
    setWWAge(age)
    if (!getWWBmi()) {
      setWWBmi(bmi)
    }
    setWWGender(gender)
    setWWHeight(height)
    setWWWeight(weight)
    setWWFeatures(features)
  }

  useEffect(() => {
    if (clientId && guid && clientSecret) {
      const currentUserId = getWWUserId()
      const partnerId = getPartnerId()
      if (currentUserId !== guid || !partnerId) {
        signupOrLoginWithUserId()
        setWWUserId(guid)
      } else {
        const token = getToken()
        if (token) {
          setToken(token)
          setWWUserId(guid)
          if (initContext.state.init) loginSuccess()
          setAutoLoginLoading(false)
        } else {
          signupOrLoginWithUserId()
          setWWUserId(guid)
        }
      }
    } else {
      setAutoLoginLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loginSuccess = () => {
    if (clientId) {
      setAmplitudeProfile({
        clientId: clientId,
        gender: gender,
        age: age,
        guid: guid,
        bmi: bmi,
        height: height,
        weight: weight,
        features: features,
      })
      updateUserWeightAndHeight(weight, height)
    }
    logAmplitudeEvent('Login Success', {
      Type: 'Mobile Web',
    })
    initContext.dispatch({
      type: 'SET_INIT',
      payload: false,
    })
  }

  const signupOrLoginWithUserId = async () => {
    setAutoLoginLoading(true)
    logAmplitudeEvent('Signup Start', {
      client_id: clientId,
      guid: guid,
      age: age,
      bmi: bmi,
      gender: gender === 1 ? 'Male' : gender === 2 ? 'Female' : 'Other',
      height: height,
      weight: weight,
    })
    const conditionParams = { muscledefense: isMuscleDefense() ? 1 : 0 }
    const response = await loginWithPartnerPromise(
      clientId,
      guid,
      clientSecret,
      age,
      bmi,
      gender,
      conditionParams,
      (error) => {
        logAmplitudeEvent('Signup Failure', {
          'Error Code': error?.code,
          'Error Message': error?.msg,
          Failure: error?.msg,
        })
      }
    )
    if (response && response.code === 200) {
      const data = _.get(response, 'data', {})
      const partnerId = data && data.partnerId
      const currentBmi = data?.bmi || bmi
      if (+partnerId === PARTNER_ID.REVIVE) {
        setWWBmi(currentBmi)
      }
      setToken((data && data.token) || '')
      setUserId((data && data.id) || '')
      setPartnerId(partnerId || '')
      amplitudeInit(partnerId)
      addGoogleTagManager(partnerId)
      setAmplitudeProfile({
        ...data,
        clientId: clientId,
        gender: gender,
        age: age,
        guid: guid,
        bmi: currentBmi,
      })
      logAmplitudeEvent('Signup Success')
      authContext.dispatch({ type: 'SET_PROFILE', payload: data })
      initContext.dispatch({
        type: 'SET_INIT',
        payload: false,
      })
      rContext.initResource()
      updateUserWeightAndHeight(weight, height)
    } else {
      logAmplitudeEvent('Signup Failure', {
        'Error Code': response?.code,
        'Error Message': response?.msg,
        Failure: response?.msg,
      })
      window.location.href = getErrorPagePath()
    }
    setAutoLoginLoading(false)
  }
  const updateUserWeightAndHeight = async (weight, height) => {
    const data = {}
    if (height) {
      data.height = height
      data.heightUnit = 'cm'
    }
    if (weight) {
      data.weight = weight
      data.weightUnit = 'kg'
    }
    if (_.isEmpty(data)) return
    uploadProfilePromise(data)
  }
  return { autoLoginLoading, planId }
}
